import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import {
  API_URL,
  EditableCourt,
  buildEditableCourts,
  getAuthToken,
  getVenueQuery
} from './internalIntegrationUtils';

const EXPECTED_SLUG = 'prairie-artisan-ales-okc';
const BOOKING_FLOW_URL = 'https://app.squareup.com/appointments/book/0qcyrkxpeqdi6l/CX2K8WMJKER2E/start';
const UNIT_TOKEN = 'CX2K8WMJKER2E';
const COURT_NAME = 'Pickleball Court';
const DEFAULT_RATE_CENTS = 1500;
const DAYPARTS = [
  {
    serviceNameIncludes: 'Monday-Thursday Before 4pm',
    variationName: '1 Hour',
    dayWindows: {
      1: { open: '11:00', close: '16:00' },
      2: { open: '11:00', close: '16:00' },
      3: { open: '11:00', close: '16:00' },
      4: { open: '11:00', close: '16:00' }
    }
  },
  {
    serviceNameIncludes: 'Monday-Thursday After 4pm',
    variationName: '1 Hour',
    dayWindows: {
      1: { open: '16:00', close: '22:00' },
      2: { open: '16:00', close: '22:00' },
      3: { open: '16:00', close: '22:00' },
      4: { open: '16:00', close: '22:00' }
    }
  },
  {
    serviceNameIncludes: 'Friday-Sunday Anytime',
    variationName: '1 Hour',
    dayWindows: {
      0: { open: '11:00', close: '22:00' },
      5: { open: '11:00', close: '22:00' },
      6: { open: '11:00', close: '22:00' }
    }
  }
];

type Step = 'form' | 'loading' | 'review' | 'saving' | 'success';

export default function PrairieBrewingIntegration({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>('form');
  const [error, setError] = useState('');
  const [venueName, setVenueName] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [editableCourts, setEditableCourts] = useState<EditableCourt[]>([]);
  const token = getAuthToken();

  useEffect(() => {
    let cancelled = false;
    api.getVenue()
      .then(({ venue }) => {
        if (cancelled) return;
        setVenueName(venue.name);
        if (venue.slug !== EXPECTED_SLUG) {
          setError(`Select Prairie Artisan Ales before using this integration. Current venue: ${venue.name}.`);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load the selected venue.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handlePreview = async () => {
    if (error) return;

    setStep('loading');
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/integrations/prairiebrewing/preview${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      setPreviewResult(data);
      setEditableCourts(buildEditableCourts(data.data?.courts || [], DEFAULT_RATE_CENTS));
      setStep('review');
    } catch (err: any) {
      setError(err.message || 'Preview failed');
      setStep('form');
    }
  };

  const handleCourtNameChange = (index: number, name: string) => {
    const updated = [...editableCourts];
    updated[index] = { ...updated[index], name };
    setEditableCourts(updated);
  };

  const handleCourtRateChange = (index: number, cents: number) => {
    const updated = [...editableCourts];
    updated[index] = { ...updated[index], hourlyRateCents: cents };
    setEditableCourts(updated);
  };

  const handleImport = async () => {
    setStep('saving');
    setError('');

    try {
      const metadata = previewResult?.data?.metadata || {};
      const res = await fetch(`${API_URL}/api/integrations/import${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          integrationType: 'prairiebrewing',
          externalId: UNIT_TOKEN,
          config: {
            bookingFlowUrl: metadata.bookingFlowUrl || BOOKING_FLOW_URL,
            unitToken: metadata.unitToken || UNIT_TOKEN,
            venueName: previewResult?.data?.venueName || 'Prairie Artisan Ales',
            courtCount: editableCourts.length || 1,
            slotIntervalMinutes: 60,
            dayparts: DAYPARTS,
            courtName: COURT_NAME,
            courtType: 'indoor'
          },
          courts: editableCourts,
          schedule: [],
          operatingHours: previewResult?.data?.operatingHours || [],
          rawAvailability: previewResult?.raw?.availability || []
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Import failed');
      setStep('review');
    }
  };

  if (step === 'success') {
    return (
      <div className="integration-setup">
        <div className="integration-setup__header">
          <button className="integration-setup__back" onClick={onClose}>← Back</button>
          <h2>Prairie Artisan Ales</h2>
        </div>
        <div className="integration-setup__success">
          <div className="integration-setup__success-icon">✓</div>
          <h3>Connected!</h3>
          <p>Prairie Artisan Ales availability is now syncing through Square Appointments.</p>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="integration-setup">
      <div className="integration-setup__header">
        <button className="integration-setup__back" onClick={onClose}>← Back</button>
        <h2>Prairie Artisan Ales</h2>
        <span className="integration-badge" style={{ background: '#6366f1', color: '#fff', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>Internal</span>
      </div>

      {step === 'form' || step === 'loading' ? (
        <div className="integration-setup__body">
          <p className="integration-setup__description">
            Connect Prairie Artisan Ales through its public Square Appointments flow. This setup is locked to the Prairie venue and imports the live pickleball court availability feed.
          </p>
          <div className="integration-setup__field">
            <label>Selected Venue</label>
            <input type="text" value={venueName || 'Loading...'} readOnly />
          </div>
          <div className="integration-setup__field">
            <label>Booking Backend</label>
            <input type="text" value="Square Appointments / unit CX2K8WMJKER2E" readOnly />
          </div>

          {error && <div className="integration-setup__error">{error}</div>}

          <div className="integration-setup__actions">
            <button className="btn btn-primary" onClick={handlePreview} disabled={step === 'loading' || !!error}>
              {step === 'loading' ? 'Loading...' : 'Preview'}
            </button>
            <button className="btn btn-secondary" onClick={onClose} disabled={step === 'loading'}>
              Cancel
            </button>
          </div>
        </div>
      ) : step === 'review' || step === 'saving' ? (
        <div className="integration-setup__body">
          <p className="integration-setup__description">
            Found <strong>{editableCourts.length} court{editableCourts.length !== 1 ? 's' : ''}</strong>.
            Review and edit names and rates before connecting.
          </p>

          <div className="integration-setup__courts">
            {editableCourts.map((court, index) => (
              <div key={court.id || index} className="integration-setup__court-row">
                <input
                  type="text"
                  value={court.name}
                  onChange={(e) => handleCourtNameChange(index, e.target.value)}
                  className="integration-setup__court-name"
                  disabled={step === 'saving'}
                />
                <span className="integration-setup__court-type">{court.type}</span>
                <div className="integration-setup__court-rate">
                  <span>$</span>
                  <input
                    type="number"
                    value={court.hourlyRateCents / 100}
                    onChange={(e) => handleCourtRateChange(index, Math.round(parseFloat(e.target.value || '0') * 100))}
                    min={0}
                    step={1}
                    className="integration-setup__rate-input"
                    disabled={step === 'saving'}
                  />
                  <span>/hr</span>
                </div>
              </div>
            ))}
          </div>

          {error && <div className="integration-setup__error">{error}</div>}

          <div className="integration-setup__actions">
            <button className="btn btn-primary" onClick={handleImport} disabled={step === 'saving'}>
              {step === 'saving' ? 'Connecting...' : 'Connect'}
            </button>
            <button className="btn btn-secondary" onClick={() => setStep('form')} disabled={step === 'saving'}>
              Back
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
